import React, { useState, useEffect, useCallback, useRef } from 'react';
import './ZoomControls.css';

const ZoomControls = ({ mapRef, onToggle3D, is3DEnabled, onCurrentLocation, streetView, roadQuality, events, onToggleDesigner, isDesignerOpen, onToggleStylePanel, isStylePanelOpen, onToggleBuilding3DPanel, isBuilding3DPanelOpen, roadWidth }) => {
  const [zoomLevel, setZoomLevel] = useState(null);
  const [zoomInputValue, setZoomInputValue] = useState('');
  const [isEditingZoom, setIsEditingZoom] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isBuildingNamesVisible, setIsBuildingNamesVisible] = useState(false);
  const isBuildingNamesVisibleRef = useRef(false);
  const isAimapsPublic = typeof window !== 'undefined'
    && window.location.origin === 'https://maps.delhivery.com'
    && window.location.pathname === '/';
  
  // Ruler measurement state
  const [isRulerMode, setIsRulerMode] = useState(false);
  const [rulerLength, setRulerLength] = useState(100); // Default 100px
  const [rulerOrientation, setRulerOrientation] = useState('horizontal'); // 'horizontal' or 'vertical'
  const [showRulerSettings, setShowRulerSettings] = useState(false);
  const rulerCursorRef = useRef(null);

  // Update zoom level from the map's current state
  const updateZoomLevel = useCallback(() => {
    if (mapRef?.current) {
      setZoomLevel(mapRef.current.getZoom());
    }
  }, [mapRef]);

  // Wait for map ref to become available (map loads asynchronously)
  useEffect(() => {
    const checkMapReady = () => {
      if (mapRef?.current) {
        setIsMapReady(true);
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkMapReady()) return;

    // Poll for map ref if not immediately available
    const interval = setInterval(() => {
      if (checkMapReady()) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [mapRef]);

  // Subscribe to map zoom events for real-time updates
  useEffect(() => {
    if (!isMapReady || !mapRef?.current) return;

    const map = mapRef.current;

    // Set initial zoom level
    updateZoomLevel();

    // Listen to 'zoom' event for real-time updates during zooming
    map.on('zoom', updateZoomLevel);

    // Cleanup listener on unmount
    return () => {
      map.off('zoom', updateZoomLevel);
    };
  }, [isMapReady, mapRef, updateZoomLevel]);

  // Keep input synced with map zoom unless user is actively typing.
  useEffect(() => {
    if (zoomLevel !== null && !isEditingZoom) {
      setZoomInputValue(zoomLevel.toFixed(1));
    }
  }, [zoomLevel, isEditingZoom]);

  const handleZoomIn = () => {
    if (!mapRef?.current) return;
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    map.easeTo({
      zoom: Math.min(currentZoom + 1, map.getMaxZoom()),
      duration: 200,
      easing: t => t * (2 - t)
    });
  };

  const handleZoomOut = () => {
    if (!mapRef?.current) return;
    const map = mapRef.current;
    const currentZoom = map.getZoom();
    map.easeTo({
      zoom: Math.max(currentZoom - 1, map.getMinZoom()),
      duration: 200,
      easing: t => t * (2 - t)
    });
  };

  const handleZoomInputApply = () => {
    if (!mapRef?.current) return;

    const map = mapRef.current;
    const parsedZoom = Number.parseFloat(zoomInputValue);

    if (Number.isNaN(parsedZoom)) {
      setZoomInputValue(zoomLevel !== null ? zoomLevel.toFixed(1) : '');
      setIsEditingZoom(false);
      return;
    }

    const clampedZoom = Math.min(Math.max(parsedZoom, map.getMinZoom()), map.getMaxZoom());
    map.easeTo({
      zoom: clampedZoom,
      duration: 250,
      easing: t => t * (2 - t)
    });

    setZoomInputValue(clampedZoom.toFixed(1));
    setIsEditingZoom(false);
  };

  const handleZoomInputKeyDown = (e) => {
    e.stopPropagation();

    if (e.key === 'Enter') {
      e.preventDefault();
      handleZoomInputApply();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setZoomInputValue(zoomLevel !== null ? zoomLevel.toFixed(1) : '');
      setIsEditingZoom(false);
      e.currentTarget.blur();
    }
  };

  const handleCurrentLocation = () => {
    if (onCurrentLocation) {
      onCurrentLocation();
    }
  };

  const handleToggleBuildingNames = () => {
    if (!mapRef?.current) return;
    const map = mapRef.current;
    const newVisible = !isBuildingNamesVisible;

    // Toggle POI layers normally
    const poiLayers = ['pois_important', 'pois_amenity', 'pois_shop_tourism_leisure'];
    poiLayers.forEach(layerId => {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', newVisible ? 'visible' : 'none');
      }
    });

    if (newVisible) {
      // Query all rendered building features and deduplicate by name
      const buildingFeatures = map.querySourceFeatures('tegola', { sourceLayer: 'buildings' });
      const seen = new Set();
      const dedupedFeatures = [];
      for (const f of buildingFeatures) {
        const name = f.properties?.name;
        if (!name) continue;
        if (seen.has(name)) continue;
        seen.add(name);
        // Use the centroid of the bounding box as the label point
        const coords = f.geometry?.coordinates;
        if (!coords) continue;
        let allPts = [];
        const flatten = (arr) => {
          if (typeof arr[0] === 'number') { allPts.push(arr); return; }
          for (const item of arr) flatten(item);
        };
        flatten(coords);
        if (allPts.length === 0) continue;
        const sumLng = allPts.reduce((s, p) => s + p[0], 0);
        const sumLat = allPts.reduce((s, p) => s + p[1], 0);
        dedupedFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [sumLng / allPts.length, sumLat / allPts.length] },
          properties: { name }
        });
      }

      const geojson = { type: 'FeatureCollection', features: dedupedFeatures };

      if (map.getSource('buildings_names_deduped')) {
        map.getSource('buildings_names_deduped').setData(geojson);
      } else {
        map.addSource('buildings_names_deduped', { type: 'geojson', data: geojson });
      }

      if (!map.getLayer('buildings_labels_deduped')) {
        map.addLayer({
          id: 'buildings_labels_deduped',
          type: 'symbol',
          source: 'buildings_names_deduped',
          minzoom: 17,
          layout: {
            'text-field': ['get', 'name'],
            'text-font': ['Noto Sans Regular'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 16, 7.2, 17, 8.8, 20, 10.4],
            'text-padding': 8,
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-optional': true
          },
          paint: {
            'text-color': '#555',
            'text-halo-color': '#FFFFFF',
            'text-halo-width': 1.5,
            'text-halo-blur': 0
          }
        });
      } else {
        map.setLayoutProperty('buildings_labels_deduped', 'visibility', 'visible');
      }

      // Refresh deduped labels when map moves
      const refreshDeduped = () => {
        if (!isBuildingNamesVisibleRef.current) return;
        const features = map.querySourceFeatures('tegola', { sourceLayer: 'buildings' });
        const seenNames = new Set();
        const deduped = [];
        for (const f of features) {
          const n = f.properties?.name;
          if (!n || seenNames.has(n)) continue;
          seenNames.add(n);
          const c = f.geometry?.coordinates;
          if (!c) continue;
          let pts = [];
          const flat = (arr) => { if (typeof arr[0] === 'number') { pts.push(arr); return; } for (const i of arr) flat(i); };
          flat(c);
          if (pts.length === 0) continue;
          const sLng = pts.reduce((s, p) => s + p[0], 0);
          const sLat = pts.reduce((s, p) => s + p[1], 0);
          deduped.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [sLng / pts.length, sLat / pts.length] },
            properties: { name: n }
          });
        }
        const src = map.getSource('buildings_names_deduped');
        if (src) src.setData({ type: 'FeatureCollection', features: deduped });
      };
      map.on('moveend', refreshDeduped);
      map._bnRefreshHandler = refreshDeduped;
    } else {
      // Hide deduped layer
      if (map.getLayer('buildings_labels_deduped')) {
        map.setLayoutProperty('buildings_labels_deduped', 'visibility', 'none');
      }
      // Remove moveend listener
      if (map._bnRefreshHandler) {
        map.off('moveend', map._bnRefreshHandler);
        delete map._bnRefreshHandler;
      }
    }

    setIsBuildingNamesVisible(newVisible);
    isBuildingNamesVisibleRef.current = newVisible;
  };

  const handleStreetView = () => {
    if (!streetView) return;
    
    const newState = !streetView.isStreetViewEnabled;
    streetView.setIsStreetViewEnabled(newState);
    
    // If enabling street view, pan to the target location
    if (newState && mapRef.current) {
      // Target location: lat 28.25, lon 76.85 (center of the example area)
      const targetLat = 28.25;
      const targetLon = 76.85;
      const targetZoom = 14;
      
      mapRef.current.flyTo({
        center: [targetLon, targetLat],
        zoom: targetZoom,
        duration: 1500,
        essential: true
      });
    }
  };

  const handleRoadQuality = () => {
    if (!roadQuality) return;
    roadQuality.toggleRoadQuality();
  };

  const handleEvents = () => {
    if (!events) return;
    events.toggleEvents();
  };

  const handleDesigner = () => {
    if (onToggleDesigner) {
      onToggleDesigner();
    }
  };

  const handleRoadWidth = () => {
    if (roadWidth) {
      roadWidth.togglePanel();
    }
  };

  const handleStylePanel = () => {
    if (onToggleStylePanel) {
      onToggleStylePanel();
    }
  };

  const handleBuilding3DPanel = () => {
    if (onToggleBuilding3DPanel) {
      onToggleBuilding3DPanel();
    }
  };

  // Ruler measurement handlers
  const handleRulerToggle = () => {
    if (isRulerMode) {
      // Disable ruler mode
      setIsRulerMode(false);
      setShowRulerSettings(false);
      removeRulerCursor();
    } else {
      // Enable ruler mode
      setIsRulerMode(true);
      setShowRulerSettings(true);
    }
  };

  const createRulerCursor = useCallback(() => {
    // Remove existing cursor if any
    removeRulerCursor();

    // Create ruler cursor element
    const cursor = document.createElement('div');
    cursor.className = `ruler-cursor ${rulerOrientation === 'vertical' ? 'ruler-vertical' : ''}`;
    
    if (rulerOrientation === 'vertical') {
      cursor.style.height = `${rulerLength}px`;
      cursor.style.width = 'auto';
    } else {
      cursor.style.width = `${rulerLength}px`;
      cursor.style.height = 'auto';
    }
    
    cursor.innerHTML = `
      <div class="ruler-line"></div>
      <div class="ruler-cap ruler-cap-left"></div>
      <div class="ruler-cap ruler-cap-right"></div>
      <div class="ruler-label">${rulerLength}px</div>
    `;
    document.body.appendChild(cursor);
    rulerCursorRef.current = cursor;

    // Add mouse move listener
    const handleMouseMove = (e) => {
      if (rulerCursorRef.current) {
        rulerCursorRef.current.style.left = `${e.clientX}px`;
        rulerCursorRef.current.style.top = `${e.clientY}px`;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    rulerCursorRef.current._mouseMoveHandler = handleMouseMove;
  }, [rulerLength, rulerOrientation]);

  const removeRulerCursor = () => {
    if (rulerCursorRef.current) {
      if (rulerCursorRef.current._mouseMoveHandler) {
        document.removeEventListener('mousemove', rulerCursorRef.current._mouseMoveHandler);
      }
      rulerCursorRef.current.remove();
      rulerCursorRef.current = null;
    }
  };

  // Update ruler cursor when length changes or mode is enabled
  useEffect(() => {
    if (isRulerMode) {
      createRulerCursor();
    } else {
      removeRulerCursor();
    }

    return () => {
      removeRulerCursor();
    };
  }, [isRulerMode, rulerLength, rulerOrientation, createRulerCursor]);

  const handleRulerLengthChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0 && value <= 1000) {
      setRulerLength(value);
    }
  };

  return (
    <div className="zoom-controls-container">
      {/* Current Location Button */}
      <button 
        className="zoom-control-location-btn"
        onClick={handleCurrentLocation}
        title="Go to current location"
      >
        <img src="/icons/_v1locationPointer.png" alt="Current Location" className="zoom-control-location-icon" />
      </button>

      {/* Building Names Toggle */}
      <button
        className={`zoom-control-bn-btn ${isBuildingNamesVisible ? 'active' : ''}`}
        onClick={handleToggleBuildingNames}
        title={isBuildingNamesVisible ? 'Hide Building Names' : 'Show Building Names'}
      >
        <span>BN</span>
      </button>

      {!isAimapsPublic && (
        <>
          {/* Street View Button */}
          <button 
            className={`zoom-control-street-view-btn ${streetView?.isStreetViewEnabled ? 'active' : ''}`}
            onClick={handleStreetView}
            title={streetView?.isStreetViewEnabled ? 'Hide Street View' : 'Show Street View'}
          >
            <img src="/icons/_v1streetViewIcon.png" alt="Street View" className="zoom-control-street-view-icon" />
          </button>

          {/* Road Quality Button */}
          <button
            className={`zoom-control-road-quality-btn ${roadQuality?.isRoadQualityEnabled ? 'active' : ''}`}
            onClick={handleRoadQuality}
            title={roadQuality?.isRoadQualityEnabled ? 'Hide Road Quality View' : 'Show Road Quality View'}
          >
            <span className="road-quality-q-text">Q</span>
          </button>
        </>
      )}

      {/* Live Events Button (protests, floods, closures…) */}
      <button
        className={`zoom-control-events-btn ${events?.isEventsEnabled ? 'active' : ''}`}
        onClick={handleEvents}
        title={events?.isEventsEnabled ? 'Hide Live Events' : 'Show Live Events'}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 4 L21 19 H3 Z" />
          <line x1="12" y1="10" x2="12" y2="14" />
          <circle cx="12" cy="16.5" r="0.6" fill="currentColor" />
        </svg>
      </button>

      {/* 3D Button */}
      {!isAimapsPublic && (
        <button 
          className={`zoom-control-3d-btn ${is3DEnabled ? 'active' : ''}`}
          onClick={onToggle3D}
          title="Toggle 3D View"
        >
          <img src="/icons/_v13dBox.png" alt="3D" className="zoom-control-3d-icon" />
        </button>
      )}

      {!isAimapsPublic && (
        <>
          {/* Map Designer Button */}
          <button 
            className={`zoom-control-designer-btn ${isDesignerOpen ? 'active' : ''}`}
            onClick={handleDesigner}
            title="Customize Map Colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C12.55 22 13 21.55 13 21C13 20.76 12.92 20.55 12.78 20.38C12.65 20.22 12.56 20.01 12.56 19.78C12.56 19.23 13.01 18.78 13.56 18.78H15.33C18.46 18.78 21 16.24 21 13.11C21 6.89 17.05 2 12 2Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="7.5" cy="11.5" r="1.5" fill="#E74C3C"/>
              <circle cx="10.5" cy="7.5" r="1.5" fill="#F39C12"/>
              <circle cx="14.5" cy="7.5" r="1.5" fill="#27AE60"/>
              <circle cx="17.5" cy="11.5" r="1.5" fill="#3498DB"/>
            </svg>
          </button>

          {/* Road Width Control Button */}
          <button 
            className={`zoom-control-road-width-btn ${roadWidth?.isPanelOpen ? 'active' : ''}`}
            onClick={handleRoadWidth}
            title="Adjust Road Widths"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H21" stroke="#333333" strokeWidth="3" strokeLinecap="round"/>
              <path d="M5 12H19" stroke="#333333" strokeWidth="2" strokeLinecap="round"/>
              <path d="M7 18H17" stroke="#333333" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          <button
            className={`zoom-control-style-json-btn ${isStylePanelOpen ? 'active' : ''}`}
            onClick={handleStylePanel}
            title="Tegola JSON Style"
          >
            <span>{'{ }'}</span>
          </button>

          <button
            className={`zoom-control-building-3d-btn ${isBuilding3DPanelOpen ? 'active' : ''}`}
            onClick={handleBuilding3DPanel}
            title="Building Style Settings"
          >
            <span>B3</span>
          </button>

          {/* Ruler Measurement Tool Button */}
          <div className="ruler-tool-wrapper">
            <button 
              className={`zoom-control-ruler-btn ${isRulerMode ? 'active' : ''}`}
              onClick={handleRulerToggle}
              title="Measurement Ruler Tool"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 6L3 6" stroke="#333333" strokeWidth="2" strokeLinecap="round"/>
                <path d="M21 6V10" stroke="#333333" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 6V10" stroke="#333333" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 6V8" stroke="#333333" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M11 6V9" stroke="#333333" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M15 6V8" stroke="#333333" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M19 6V8" stroke="#333333" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            
            {/* Ruler Settings Popup */}
            {showRulerSettings && (
              <div className="ruler-settings-popup">
                <div className="ruler-settings-header">
                  <span>Ruler Settings</span>
                  <button 
                    className="ruler-settings-close"
                    onClick={() => setShowRulerSettings(false)}
                  >
                    ×
                  </button>
                </div>
                
                {/* Orientation Toggle */}
                <div className="ruler-orientation-toggle">
                  <button 
                    className={`ruler-orientation-btn ${rulerOrientation === 'horizontal' ? 'active' : ''}`}
                    onClick={() => setRulerOrientation('horizontal')}
                    title="Horizontal"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M3 12V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M21 12V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                  <button 
                    className={`ruler-orientation-btn ${rulerOrientation === 'vertical' ? 'active' : ''}`}
                    onClick={() => setRulerOrientation('vertical')}
                    title="Vertical"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M12 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M12 21H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                
                <div className="ruler-settings-label">Length</div>
                <div className="ruler-settings-content">
                  <input
                    type="range"
                    min="20"
                    max="500"
                    value={rulerLength}
                    onChange={handleRulerLengthChange}
                    className="ruler-length-slider"
                  />
                  <div className="ruler-length-input-wrapper">
                    <input
                      type="number"
                      min="20"
                      max="1000"
                      value={rulerLength}
                      onChange={handleRulerLengthChange}
                      className="ruler-length-input"
                    />
                    <span className="ruler-length-unit">px</span>
                  </div>
                </div>
                <div className="ruler-presets">
                  <button onClick={() => setRulerLength(50)}>50</button>
                  <button onClick={() => setRulerLength(100)}>100</button>
                  <button onClick={() => setRulerLength(200)}>200</button>
                  <button onClick={() => setRulerLength(300)}>300</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Zoom Controls */}
      <div className="zoom-control-buttons-container">
        {/* Zoom In */}
        <button 
          className="zoom-control-btn zoom-in"
          onClick={handleZoomIn}
          title="Zoom In"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 4V16M4 10H16" stroke="#333333" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Divider */}
        <div className="zoom-control-divider" />

        {/* Zoom Out */}
        <button 
          className="zoom-control-btn zoom-out"
          onClick={handleZoomOut}
          title="Zoom Out"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 10H16" stroke="#333333" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {/* Zoom Level Display */}
      {zoomLevel !== null && (
        <input
          type="text"
          inputMode="decimal"
          className="zoom-level-input"
          title="Enter zoom level and press Enter"
          value={zoomInputValue}
          onFocus={() => setIsEditingZoom(true)}
          onChange={(e) => setZoomInputValue(e.target.value)}
          onKeyDown={handleZoomInputKeyDown}
          onBlur={() => {
            setIsEditingZoom(false);
            setZoomInputValue(zoomLevel.toFixed(1));
          }}
        />
      )}
    </div>
  );
};

export default ZoomControls;
